import React from 'react';
import withShipment from '../../../withShipment';

import SearchableTable from '../../global/Search/SearchableTable';
import SaveIcon from '@material-ui/icons/Save';
import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';
import FeedbackBar from '../../feedbackBar/FeedbackBar';
import {
  ENABLE_MATCH_RULE,
  MANAGE_RULE,
  RULE_DESCRIPTION,
  SAVE,
  ENABLE_NFC_TICKET_OUT,
  NFC_TICKET_OUT_DESCRIPTION,
  ENABLE_AUDIT_VERIFICATION,
  AUDIT_VERIFICATION_DESCRIPTION,
  ENABLE_TICKET_PHOTO,
  TICKET_PHOTO_DESCRIPTION,
  ENABLE_VIP_MATCH,
  VIP_MATCH_DESCRIPTION,
  VIP_MATCH_POINT_PRESETS,
  DEFAULT_VIP_MATCH_POINTS,
  REQUIRE_PHONE_VERIFICATION,
  PHONE_VERIFICATION_DESCRIPTION,
  ENABLE_NUVE_BOOST,
  NUVE_BOOST_DESCRIPTION,
  VALIDATION_BANNER,
  REQUIRED_FIELD,
  MUST_BE_POSITIVE,
  MUST_BE_NON_NEGATIVE,
  INVALID_TIME,
  INVALID_TIME_LIST,
  TOGGLE_FIELDS,
} from './constants';
import { userInfoSelector } from '../../../redux/selectors/auth';
import { TextField } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import { listRules, saveRules } from '../../../redux/actions/settings';
import { refreshUserInfo } from '../../../redux/actions/auth';
import { clearStatus } from '../../../redux/actions/global';
import { rulesSelector } from '../../../redux/selectors/settings';
import {
  getTicketPhotoSetting,
  saveTicketPhotoSetting,
} from '../../../redux/actions/ticketOut';
import { ticketPhotoSettingSelector } from '../../../redux/selectors/ticketOut';
import {
  DEFAULT_TICKET_PHOTO_SETTING,
  TICKET_THRESHOLD_PRESETS,
} from '../../ticketOut/constants';
import './ManageRule.css';
import WriteGuard from '../../global/ReadOnly/WriteGuard';

// A single 24-hour clock value. "24:00" is accepted because the server
// reads it as midnight of the next day (see CustomerService slot parsing).
const TIME_PATTERN = /^(?:(?:[01]?[0-9]|2[0-3]):[0-5][0-9]|24:0?0)$/;
const isBlank = (value) => String(value == null ? '' : value).trim() === '';
const isWholeNumber = (value) => /^[0-9]+$/.test(String(value).trim());

class ManageRule extends SearchableTable {
  constructor(props) {
    super(props);
    this.state = {
      machines: this.props.machines || [],
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
      enableMatchRule: false,
      ruleName: '',
      timeLimit: '',
      maxMatchesPerDay: '',
      resetTimes: '',
      maxCountResetTime: '',
      maxCountReset: '',
      // NFC ticket-out is on by default; the toggle only appears when
      // the owning company has the NFC feature enabled.
      nfcTicketOutEnabled: true,
      companyNfcEnabled: false,
      enableAuditVerification: false,
      ticketPhotoEnabled: DEFAULT_TICKET_PHOTO_SETTING.enabled,
      ticketPhotoThreshold: String(
        DEFAULT_TICKET_PHOTO_SETTING.thresholdAmount
      ),
      vipMatchEnabled: false,
      vipMatchPoints: String(DEFAULT_VIP_MATCH_POINTS),
      // Phone verification is off by default; admins opt a
      // location in by checking this.
      requirePhoneVerification: false,
      // NuVe Boost menu visibility, location-level. Enabled by default so
      // Boost-eligible locations keep the menu until an admin opts out. The
      // toggle only renders when the company's plan includes Boost
      // (companyBoostEligible).
      enableNuveBoost: true,
      companyBoostEligible: false,
      // field name -> message. Filled by a failed Save attempt, cleared per
      // field as the user fixes it.
      errors: {},
    };
  }

  componentDidMount() {
    this.props.listRules({
      locationId: this.props.userInfo.selectedLocationId,
    });
    this.props.getTicketPhotoSetting({
      locationId: this.props.userInfo.selectedLocationId,
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.rules !== this.props.rules) {
      if (Array.isArray(this.props.rules) && this.props.rules.length > 0) {
        const rule = this.props.rules[0];
        this.setState({
          ruleName: rule.ruleName,
          timeLimit: rule.timeLimit,
          enableMatchRule: rule.enableMatchRule,
          maxMatchesPerDay: rule.maxMatchesPerDay,
          resetTimes: rule.resetTimes,
          maxCountResetTime: rule.maxCountResetTime,
          nfcTicketOutEnabled: rule.nfcTicketOutEnabled !== false,
          companyNfcEnabled: rule.companyNfcEnabled === true,
          enableAuditVerification: rule.enableAuditVerification === true,
          vipMatchEnabled: rule.vipMatchEnabled === true,
          vipMatchPoints:
            rule.vipMatchPoints && rule.vipMatchPoints !== ''
              ? String(rule.vipMatchPoints)
              : String(DEFAULT_VIP_MATCH_POINTS),
          requirePhoneVerification: rule.requirePhoneVerification === true,
          enableNuveBoost: rule.enableNuveBoost !== false,
          companyBoostEligible: rule.companyBoostEligible === true,
        });
      }
    }

    if (
      prevProps.ticketPhotoSetting !== this.props.ticketPhotoSetting &&
      this.props.ticketPhotoSetting
    ) {
      const setting = this.props.ticketPhotoSetting;
      this.setState({
        ticketPhotoEnabled:
          setting.enabled != null
            ? setting.enabled
            : DEFAULT_TICKET_PHOTO_SETTING.enabled,
        ticketPhotoThreshold:
          setting.thresholdAmount != null
            ? String(setting.thresholdAmount)
            : String(DEFAULT_TICKET_PHOTO_SETTING.thresholdAmount),
      });
    }
  }

  getContextualMenuItems = () => [
    {
      title: SAVE,
      icon: <SaveIcon />,
      onClick: this.handleSave,
      disabled: this.isSaveDisabled(),
    },
  ];

  // Each checkbox is a promise that the feature is configured, so its
  // revealed fields become mandatory while it is checked. An empty cooldown
  // or max-matches blows up the match check server-side, an unparseable
  // reset time throws on LocalTime.parse, and an empty VIP minimum leaves
  // the VIP photo requirement silently inert — all of which look like the
  // rule "saved fine" until a customer hits it.
  validateForm = () => {
    const errors = {};

    if (this.state.enableMatchRule) {
      if (isBlank(this.state.ruleName)) {
        errors.ruleName = REQUIRED_FIELD;
      }

      if (isBlank(this.state.timeLimit)) {
        errors.timeLimit = REQUIRED_FIELD;
      } else if (
        !isWholeNumber(this.state.timeLimit) ||
        Number(this.state.timeLimit) <= 0
      ) {
        errors.timeLimit = MUST_BE_POSITIVE;
      }

      if (isBlank(this.state.maxMatchesPerDay)) {
        errors.maxMatchesPerDay = REQUIRED_FIELD;
      } else if (
        !isWholeNumber(this.state.maxMatchesPerDay) ||
        Number(this.state.maxMatchesPerDay) <= 0
      ) {
        errors.maxMatchesPerDay = MUST_BE_POSITIVE;
      }

      if (isBlank(this.state.resetTimes)) {
        errors.resetTimes = REQUIRED_FIELD;
      } else {
        const slots = String(this.state.resetTimes)
          .split(',')
          .map((slot) => slot.trim());
        if (slots.some((slot) => !TIME_PATTERN.test(slot))) {
          errors.resetTimes = INVALID_TIME_LIST;
        }
      }

      if (isBlank(this.state.maxCountResetTime)) {
        errors.maxCountResetTime = REQUIRED_FIELD;
      } else if (
        !TIME_PATTERN.test(String(this.state.maxCountResetTime).trim())
      ) {
        errors.maxCountResetTime = INVALID_TIME;
      }
    }

    if (this.state.ticketPhotoEnabled) {
      // 0 is a legitimate threshold here ("photo on every ticket out"), so
      // only blank / non-numeric input is rejected.
      if (isBlank(this.state.ticketPhotoThreshold)) {
        errors.ticketPhotoThreshold = REQUIRED_FIELD;
      } else if (!isWholeNumber(this.state.ticketPhotoThreshold)) {
        errors.ticketPhotoThreshold = MUST_BE_NON_NEGATIVE;
      }
    }

    if (this.state.vipMatchEnabled) {
      if (isBlank(this.state.vipMatchPoints)) {
        errors.vipMatchPoints = REQUIRED_FIELD;
      } else if (
        !isWholeNumber(this.state.vipMatchPoints) ||
        Number(this.state.vipMatchPoints) <= 0
      ) {
        // A 0 minimum would flag every customer as VIP, and the Dashboard
        // treats a falsy threshold as "feature off" anyway.
        errors.vipMatchPoints = MUST_BE_POSITIVE;
      }
    }

    return errors;
  };

  // Drops a field's error as soon as the user edits it, so the form stops
  // shouting about something already fixed.
  clearError = (field) => {
    if (!this.state.errors[field]) return;
    this.setState((prevState) => {
      const errors = { ...prevState.errors };
      delete errors[field];
      return { errors };
    });
  };

  clearErrors = (fields) => {
    this.setState((prevState) => {
      const errors = { ...prevState.errors };
      fields.forEach((field) => delete errors[field]);
      return { errors };
    });
  };

  // True while a section owns at least one outstanding error, used to tint
  // its border red so the offending section is findable without scrolling.
  sectionHasError = (toggleName) =>
    (TOGGLE_FIELDS[toggleName] || []).some((field) => this.state.errors[field]);

  handleSave = () => {
    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      this.openFeedbackBar('error', VALIDATION_BANNER);
      return;
    }
    this.setState({ errors: {} });

    const callback = (error, response) => {
      if (error) {
        this.openFeedbackBar('error', error);
      } else {
        this.openFeedbackBar('success', response);
        // Re-fetch the user so a flipped NuVe Boost rule shows/hides its
        // sidebar item immediately — no re-login or location switch needed.
        this.props.refreshUserInfo();
      }
    };

    this.props.saveRules(
      {
        ruleName: this.state.ruleName,
        locationId: this.props.userInfo.selectedLocationId,
        enableMatchRule: this.state.enableMatchRule,
        timeLimit: this.state.timeLimit,
        resetTimes: this.state.resetTimes,
        maxMatchesPerDay: this.state.maxMatchesPerDay,
        maxCountResetTime: this.state.maxCountResetTime,
        // Only the company-allowed NFC value is meaningful; when the
        // company has NFC disabled this falls back to the stored default.
        nfcTicketOutEnabled: this.state.nfcTicketOutEnabled,
        enableAuditVerification: this.state.enableAuditVerification,
        vipMatchEnabled: this.state.vipMatchEnabled,
        vipMatchPoints: this.state.vipMatchPoints,
        requirePhoneVerification: this.state.requirePhoneVerification,
        enableNuveBoost: this.state.enableNuveBoost,
      },
      callback
    );

    this.props.saveTicketPhotoSetting({
      locationId: this.props.userInfo.selectedLocationId,
      enabled: this.state.ticketPhotoEnabled,
      thresholdAmount: Number(this.state.ticketPhotoThreshold || 0),
    });
  };

  handleTicketThresholdChange = (event) => {
    const { value } = event.target;
    if (value === '' || /^[0-9]+$/.test(value)) {
      this.setState({ ticketPhotoThreshold: value });
      this.clearError('ticketPhotoThreshold');
    }
  };

  setTicketThresholdPreset = (value) => {
    this.setState({ ticketPhotoThreshold: String(value) });
    this.clearError('ticketPhotoThreshold');
  };

  handleVipPointsChange = (event) => {
    const { value } = event.target;
    if (value === '' || /^[0-9]+$/.test(value)) {
      this.setState({ vipMatchPoints: value });
      this.clearError('vipMatchPoints');
    }
  };

  setVipPointsPreset = (value) => {
    this.setState({ vipMatchPoints: String(value) });
    this.clearError('vipMatchPoints');
  };

  isSaveDisabled = () => {
    return this.state.machines.some((template) => !template.machine);
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  handleChange = (e) => {
    const { name, value } = e.target;

    this.setState({ [name]: value });
    this.clearError(name);
  };

  // Toggle card styled to match Manage Company's "Enable NFC Ticket-Out"
  // control. `name` is the state key it drives.
  renderToggleCard = (name, label, hint) => {
    const on = !!this.state[name];
    return (
      <label className={`rule-toggle-card ${on ? 'rule-toggle-card--on' : ''}`}>
        <span className="rule-checkbox">
          <input
            type="checkbox"
            checked={on}
            onChange={(e) => {
              const checked = e.target.checked;
              this.setState({ [name]: checked });
              // Unchecking hides the fields, so their errors go with them.
              if (!checked && TOGGLE_FIELDS[name]) {
                this.clearErrors(TOGGLE_FIELDS[name]);
              }
            }}
          />
          <span className="rule-checkbox__box" aria-hidden="true">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 8.5 6.5 12 13 4.5" />
            </svg>
          </span>
        </span>
        <span className="rule-toggle-text">
          <span className="rule-toggle-label">{label}</span>
          <span className="rule-toggle-hint">{hint}</span>
        </span>
      </label>
    );
  };

  render() {
    return (
      <React.Fragment>
        <TopToolbar
          pageName={MANAGE_RULE}
          menuItems={this.getContextualMenuItems()}
        />

        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        {/* Every control on this screen writes a rule. The Save button is
            already dropped by the toolbar filter, so without this the
            toggles and threshold fields stayed interactive with no way to
            commit them — changes that silently evaporate. Greyed and inert
            rather than hidden: the configured rules are exactly what a
            viewer comes here to read. See constants/readOnlyAccess.js. */}
        <WriteGuard mode="disable" noTooltip>
          <div className="manage-rule-container">
            <div className="manage-rule-form-section">
              <div
                className={`rule-match-section ${
                  this.state.enableMatchRule ? 'rule-match-section--on' : ''
                } ${
                  this.sectionHasError('enableMatchRule')
                    ? 'rule-match-section--error'
                    : ''
                }`}
              >
                {this.renderToggleCard(
                  'enableMatchRule',
                  ENABLE_MATCH_RULE,
                  RULE_DESCRIPTION
                )}

                {this.state.enableMatchRule && (
                  <div className="form-fields rule-match-fields">
                    <TextField
                      className="input-manage-text-field"
                      name="ruleName"
                      label="Rule Name"
                      required
                      value={this.state.ruleName}
                      onChange={this.handleChange}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      error={!!this.state.errors.ruleName}
                      helperText={this.state.errors.ruleName || ' '}
                    />
                    <TextField
                      className="input-manage-text-field"
                      label="Cooldown (hours)"
                      name="timeLimit"
                      required
                      value={this.state.timeLimit}
                      InputLabelProps={{ shrink: true }}
                      onChange={this.handleChange}
                      variant="outlined"
                      type="number"
                      inputProps={{ min: '1', step: '1' }}
                      error={!!this.state.errors.timeLimit}
                      helperText={this.state.errors.timeLimit || ' '}
                    />

                    <TextField
                      className="input-manage-text-field"
                      label="Max Matches per Day"
                      name="maxMatchesPerDay"
                      required
                      InputLabelProps={{ shrink: true }}
                      value={this.state.maxMatchesPerDay}
                      onChange={this.handleChange}
                      type="number"
                      variant="outlined"
                      inputProps={{ min: '1', step: '1' }}
                      error={!!this.state.errors.maxMatchesPerDay}
                      helperText={this.state.errors.maxMatchesPerDay || ' '}
                    />
                    <TextField
                      className="input-manage-text-field"
                      label="Reset Times (comma-separated, e.g. 05:00,17:00)"
                      name="resetTimes"
                      required
                      value={this.state.resetTimes}
                      InputLabelProps={{ shrink: true }}
                      onChange={this.handleChange}
                      variant="outlined"
                      error={!!this.state.errors.resetTimes}
                      helperText={this.state.errors.resetTimes || ' '}
                    />

                    <TextField
                      className="input-manage-text-field"
                      label="Reset Day Time (e.g. 05:00)"
                      name="maxCountResetTime"
                      required
                      value={this.state.maxCountResetTime}
                      InputLabelProps={{ shrink: true }}
                      onChange={this.handleChange}
                      variant="outlined"
                      error={!!this.state.errors.maxCountResetTime}
                      helperText={this.state.errors.maxCountResetTime || ' '}
                    />
                  </div>
                )}
              </div>

              <div className="rule-toggle-group">
                {this.state.companyNfcEnabled &&
                  this.renderToggleCard(
                    'nfcTicketOutEnabled',
                    ENABLE_NFC_TICKET_OUT,
                    NFC_TICKET_OUT_DESCRIPTION
                  )}

                {this.renderToggleCard(
                  'enableAuditVerification',
                  ENABLE_AUDIT_VERIFICATION,
                  AUDIT_VERIFICATION_DESCRIPTION
                )}

                {this.renderToggleCard(
                  'requirePhoneVerification',
                  REQUIRE_PHONE_VERIFICATION,
                  PHONE_VERIFICATION_DESCRIPTION
                )}

                {this.state.companyBoostEligible &&
                  this.renderToggleCard(
                    'enableNuveBoost',
                    ENABLE_NUVE_BOOST,
                    NUVE_BOOST_DESCRIPTION
                  )}
              </div>

              <div
                className={`rule-match-section ${
                  this.state.ticketPhotoEnabled ? 'rule-match-section--on' : ''
                } ${
                  this.sectionHasError('ticketPhotoEnabled')
                    ? 'rule-match-section--error'
                    : ''
                }`}
              >
                {this.renderToggleCard(
                  'ticketPhotoEnabled',
                  ENABLE_TICKET_PHOTO,
                  TICKET_PHOTO_DESCRIPTION
                )}

                {this.state.ticketPhotoEnabled && (
                  <div className="form-fields rule-match-fields rule-ticket-photo-fields">
                    <TextField
                      className="input-manage-text-field"
                      label="Minimum Amount"
                      name="ticketPhotoThreshold"
                      required
                      value={this.state.ticketPhotoThreshold}
                      onChange={this.handleTicketThresholdChange}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      type="tel"
                      error={!!this.state.errors.ticketPhotoThreshold}
                      helperText={this.state.errors.ticketPhotoThreshold || ' '}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        min: '0',
                        step: '1',
                      }}
                    />
                    <div className="rule-preset-pills">
                      {TICKET_THRESHOLD_PRESETS.map((preset) => (
                        <button
                          type="button"
                          key={preset}
                          className={
                            'rule-preset-pill' +
                            (Number(this.state.ticketPhotoThreshold) === preset
                              ? ' active'
                              : '')
                          }
                          onClick={() => this.setTicketThresholdPreset(preset)}
                        >
                          ${preset.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div
                className={`rule-match-section ${
                  this.state.vipMatchEnabled ? 'rule-match-section--on' : ''
                } ${
                  this.sectionHasError('vipMatchEnabled')
                    ? 'rule-match-section--error'
                    : ''
                }`}
              >
                {this.renderToggleCard(
                  'vipMatchEnabled',
                  ENABLE_VIP_MATCH,
                  VIP_MATCH_DESCRIPTION
                )}

                {this.state.vipMatchEnabled && (
                  <div className="form-fields rule-match-fields rule-ticket-photo-fields">
                    <TextField
                      className="input-manage-text-field"
                      label="Minimum Points"
                      name="vipMatchPoints"
                      required
                      value={this.state.vipMatchPoints}
                      onChange={this.handleVipPointsChange}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      type="tel"
                      error={!!this.state.errors.vipMatchPoints}
                      helperText={this.state.errors.vipMatchPoints || ' '}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        min: '0',
                        step: '1',
                      }}
                    />
                    <div className="rule-preset-pills">
                      {VIP_MATCH_POINT_PRESETS.map((preset) => (
                        <button
                          type="button"
                          key={preset}
                          className={
                            'rule-preset-pill' +
                            (Number(this.state.vipMatchPoints) === preset
                              ? ' active'
                              : '')
                          }
                          onClick={() => this.setVipPointsPreset(preset)}
                        >
                          {preset.toLocaleString()} pts
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </WriteGuard>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  rules: rulesSelector(state),
  ticketPhotoSetting: ticketPhotoSettingSelector(state),
});

const actionCreators = {
  listRules,
  saveRules,
  clearStatus,
  getTicketPhotoSetting,
  saveTicketPhotoSetting,
  refreshUserInfo,
};
export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  ManageRule
);
